<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useFavoritesStore } from '@state/stores/favorites'
import { useMessages } from '@composables/use-messages'
import formatCount from '@utils/format-count'

const props = defineProps({
    mix: {
        type: Object,
        default: () => {},
    },
    artist: {
        type: Object,
        default: () => {},
    },
    show: {
        type: Object,
        default: () => {},
    },
    channel: {
        type: Object,
        default: () => {},
    },
    size: {
        type: String,
        default: 'small', // none, small, iconOnly, large
    },
    buttonType: {
        type: String,
        default: 'tag', // tag, transparent, primary
    },
    buttonTypeActive: {
        type: [String, Boolean],
        default: false,
    },
    displayCounter: {
        type: Boolean,
        default: true,
    },
    iconMain: {
        type: String,
        default: 'heart-filled',
    },
    iconAlt: {
        type: String,
        default: 'heart',
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const favoritesStore = useFavoritesStore()
const router = useRouter()
const getMessageText = useMessages()

const countCache = ref(0)
const isReady = ref(true)

const favCount = computed(() => props.mix && props.mix.favorite_count)

const totalCount = computed(() => props.mix && favCount.value + countCache.value)

const isLoggedIn = computed(() => authStore.loggedIn)

const isActive = computed(() => {
    if (props.mix) {
        return !!favoritesStore.mixesList.find(el => el.id === props.mix.id)
    } else if (props.artist) {
        return !!favoritesStore.artistsList.find(el => el.id === props.artist.id)
    } else if (props.show) {
        return !!favoritesStore.showsList.find(el => el.id === props.show.id)
    } else if (props.channel) {
        return !!favoritesStore.channelIds.find(el => el === props.channel.id)
    } else {
        return false
    }
})

const buttonTypeActiveEvaluated = computed(() => props.buttonTypeActive || props.buttonType)

// Interim adapter onto the T8 BaseButton API. T10 Badges (batch 2) replaces this
// component's chip look wholesale; until then the public props stay so the 12 call
// sites do not change twice.
// Follow is pinned to Size M (Figma node 359:210, Variant=Outline/Size=M) regardless
// of the `size` prop the call sites still pass ("large") — see buttonSize below.
const SIZE_MAP = { none: 's', small: 's', medium: 'm', large: 'l', 'icon-only': 'm' }
const VARIANT_MAP = { tag: 'tag', transparent: 'tertiary', primary: 'primary', follow: 'outline' }

const buttonVariant = computed(
    () => VARIANT_MAP[isActive.value ? props.buttonType : buttonTypeActiveEvaluated.value] || 'tertiary'
)
const buttonSize = computed(() => (props.buttonType === 'follow' ? 'm' : SIZE_MAP[props.size] || 'l'))
const isIconOnly = computed(() => props.size === 'icon-only')
// Follow (Buttons §05, Figma node 359:210): the check is BaseButton's LEADING
// `icon`, never `trailingIcon` — every other buttonType keeps its own
// heart/alt icon (large suppresses it, per the pre-existing rule below).
const buttonIcon = computed(() => {
    if (props.buttonType === 'follow') return isActive.value ? 'check' : ''
    if (props.size === 'large') return ''
    return isActive.value ? props.iconMain : props.iconAlt
})

function notify(messageKey, type) {
    appearanceStore.handleNotification({ message: getMessageText(messageKey), type })
}

function addMix() {
    favoritesStore
        .add({ type: 'mixes', id: props.mix.id })
        .then(_res => {
            notify('FAV_MIX_ADDED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeMix() {
    favoritesStore
        .remove({ type: 'mixes', id: props.mix.id })
        .then(_res => {
            notify('FAV_MIX_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addArtist() {
    favoritesStore
        .add({ type: 'artists', id: props.artist.id })
        .then(_res => {
            notify('FAV_ARTIST_ADDED', 'success')
        })
        .catch(_err => {
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeArtist() {
    favoritesStore
        .remove({ type: 'artists', id: props.artist.id })
        .then(_res => {
            notify('FAV_ARTIST_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addShow() {
    favoritesStore
        .add({ type: 'shows', id: props.show.id })
        .then(_res => {
            notify('FAV_SHOW_ADDED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeShow() {
    favoritesStore
        .remove({ type: 'shows', id: props.show.id })
        .then(_res => {
            notify('FAV_SHOW_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addChannel() {
    favoritesStore.toggleChannel(props.channel.id)
}

function removeChannel() {
    favoritesStore.toggleChannel(props.channel.id)
}

const add = computed(() => (props.mix ? addMix : props.artist ? addArtist : props.show ? addShow : addChannel))

const remove = computed(() =>
    props.mix ? removeMix : props.artist ? removeArtist : props.show ? removeShow : removeChannel
)

watch(isActive, () => {
    isActive.value ? countCache.value++ : countCache.value--
    isReady.value = true
})

function addRemoveFav() {
    if (isLoggedIn.value) {
        if (isReady.value) {
            isReady.value = false
            isActive.value ? remove.value() : add.value()
        }
    } else {
        router.push({ name: 'login' })
    }
}
</script>

<template>
    <BaseButton
        :active="isLoggedIn && isActive"
        :variant="buttonVariant"
        :size="buttonSize"
        :icon-only="isIconOnly"
        :label="isActive ? 'Remove from favorites' : 'Add to favorites'"
        :class="[
            'base-favourites',
            buttonType === 'transparent' ? 'base-favourites-transparent' : '',
            isActive ? 'base-favourites-active' : '',
            (mix && mix.id) || (artist && artist.id) || (show && show.id) || (channel && channel.id)
                ? ''
                : 'base-favourites-inactive',
        ]"
        :icon="buttonIcon"
        @click="addRemoveFav"
    >
        <slot v-if="!$slots['main'] && displayCounter && size !== 'none'">{{
            size !== 'icon-only' ? formatCount(totalCount > 0 ? totalCount : isActive ? 1 : 0) : ''
        }}</slot>
        <slot
            v-if="!isActive"
            name="main"
        />
        <slot
            v-else
            name="alt"
        />
    </BaseButton>
</template>

<style lang="scss" scoped>
@import '@design';

.base-favourites {
    &-transparent {
        &:hover :deep(.icon),
        &.base-favourites-active :deep(.icon) {
            color: $c-accent-default;
        }
    }
}

.base-favourites-inactive {
    pointer-events: none;
    opacity: 0.3;
    filter: grayscale(1);
}
</style>
